import type { TFunction } from 'i18next';

import type { VotingDates } from '@/types/votingDates';

/**
 * The rows of an election's Voting Dates card on the Voting Info tab.
 *
 * Mirrored in the mobile app's `src/utils/votingDates.ts` (imports aside) so both
 * clients show the same rows in the same order with the same wording. Each row
 * renders only when its field is present: ballots cached before the backend
 * sent a field simply lack it.
 */

export type VotingDateRowKey =
  | 'registrationDeadline'
  | 'registerOnlineBy'
  | 'registerByMailBy'
  | 'registerInPersonBy'
  | 'sameDayRegistration'
  | 'earlyVoting'
  | 'requestAbsenteeBallotBy'
  | 'mailBallotsSentStarting'
  | 'returnBallotByMailBy'
  | 'mailedBallotMustArriveBy'
  | 'returnBallotInPersonBy'
  | 'pollsClose';

export interface VotingDateRow {
  key: VotingDateRowKey;
  label: string;
  value: string;
  /** The row's date is before `today` — the deadline or window has closed. */
  passed?: boolean;
}

/** The device's local calendar date as `YYYY-MM-DD`, for comparing to ballot dates. */
export function localIsoDate(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** True when an ISO date (or timestamp) falls before `today` (`YYYY-MM-DD`). */
export function isDatePassed(date: string | undefined, today: string | undefined): boolean {
  if (!date || !today) return false;
  return date.slice(0, 10) < today;
}

export interface ClockTime {
  hours: number;
  minutes: number;
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
};

// Minutes always show: an hour alone renders as a bare "20" in 24-hour locales
// such as Portuguese, Russian, and Persian.
const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
};

const CLOCK_RE = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;
// The wall-clock an ISO timestamp was written in — the same reading the
// backend normalizes `polls_close_time` to.
const ISO_CLOCK_RE = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})/;

/** Read a 24-hour "HH:MM" time, or the wall-clock of an ISO timestamp. */
export function parseTimeOfDay(raw: string | undefined): ClockTime | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  const match = CLOCK_RE.exec(trimmed) ?? ISO_CLOCK_RE.exec(trimmed);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

// A time of day is set as one unit. No-break spaces keep a wrapping value from
// stranding "PM" on a line of its own (browsers format "5:00 PM" with a plain
// space), and a first-strong isolate keeps a right-to-left row from showing
// Urdu's "5:00 PM" as "PM 5:00".
const asUnit = (time: string) => `\u2068${time.replace(/ /g, '\u00a0')}\u2069`;

/** A time of day on the locale's own clock: "8:00 PM", "20:00", "오후 8:00". */
export function formatTimeOfDay(raw: string | undefined, locale: string): string | null {
  const clock = parseTimeOfDay(raw);
  if (!clock) return null;
  return asUnit(
    new Intl.DateTimeFormat(locale, TIME_OPTIONS).format(
      new Date(Date.UTC(2000, 0, 1, clock.hours, clock.minutes))
    )
  );
}

const formatDate = (date: string, locale: string) =>
  new Intl.DateTimeFormat(locale, DATE_OPTIONS).format(new Date(date));

/**
 * A deadline's date, with its time of day when one parses. Built and formatted
 * in UTC, so the election office's wall-clock never shifts with the device's
 * time zone or onto a neighboring day.
 *
 * The time is set as a unit by finding the time-only format inside the
 * combined one. CLDR builds date-time patterns by joining the two, so it is
 * found in every shipped locale; if a runtime ever formats them differently,
 * the time still shows, only without the no-break spaces and isolate.
 */
export function formatDeadline(date: string, time: string | undefined, locale: string): string {
  const clock = parseTimeOfDay(time);
  if (!clock) return formatDate(date, locale);
  const day = new Date(date);
  const at = new Date(
    Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), clock.hours, clock.minutes)
  );
  const timeText = new Intl.DateTimeFormat(locale, TIME_OPTIONS).format(at);
  return new Intl.DateTimeFormat(locale, { ...DATE_OPTIONS, ...TIME_OPTIONS })
    .format(at)
    .replace(timeText, asUnit(timeText));
}

const clockMinutes = (raw: string | undefined): number | null => {
  const clock = parseTimeOfDay(raw);
  return clock ? clock.hours * 60 + clock.minutes : null;
};

/**
 * Every row for the election, plus the curated summary (at most three) shown
 * before "Show All": registration (online → mail → shared deadline), same-day
 * (→ in-person), then early voting. With `today` (the device's local date),
 * each dated row whose date has gone by is marked `passed`.
 */
export function buildVotingDateRows(
  vd: VotingDates,
  t: TFunction,
  locale: string,
  today?: string
): { allRows: VotingDateRow[]; summaryRows: VotingDateRow[] } {
  const deadline = (date: string, time: string | undefined) => formatDeadline(date, time, locale);
  // The source's own rule for a mailed deadline. Any other value adds nothing
  // rather than a guess.
  const qualifier = (kind: string | undefined) =>
    kind === 'postmarked'
      ? t('votingInfo.votingDates.postmarked')
      : kind === 'received'
        ? t('votingInfo.votingDates.receivedBy')
        : '';

  const regRows: VotingDateRow[] = [];
  const regOnline = vd.registration_online_date;
  const regMail = vd.registration_mail_date;
  const regInPerson = vd.registration_in_person_date;
  // One shared row only when every channel closes at the same moment, so no
  // channel's own closing time is dropped.
  if (
    regOnline &&
    regOnline === regMail &&
    regMail === regInPerson &&
    clockMinutes(vd.registration_online_time) === clockMinutes(vd.registration_mail_time) &&
    clockMinutes(vd.registration_mail_time) === clockMinutes(vd.registration_in_person_time)
  ) {
    regRows.push({
      key: 'registrationDeadline',
      label: t('votingInfo.votingDates.registrationDeadline'),
      value: deadline(regOnline, vd.registration_online_time),
    });
  } else {
    if (regOnline)
      regRows.push({
        key: 'registerOnlineBy',
        label: t('votingInfo.votingDates.registerOnlineBy'),
        value: deadline(regOnline, vd.registration_online_time),
      });
    if (regMail)
      regRows.push({
        key: 'registerByMailBy',
        label: t('votingInfo.votingDates.registerByMailBy'),
        value: `${deadline(regMail, vd.registration_mail_time)}${qualifier(vd.registration_mail_postmarked_or_received)}`,
      });
    if (regInPerson)
      regRows.push({
        key: 'registerInPersonBy',
        label: t('votingInfo.votingDates.registerInPersonBy'),
        value: deadline(regInPerson, vd.registration_in_person_time),
      });
  }
  if (vd.registration_same_day) {
    regRows.push({
      key: 'sameDayRegistration',
      label: t('votingInfo.votingDates.sameDayRegistration'),
      value: t('votingInfo.votingDates.availableAtPollingPlace'),
    });
  }

  const earlyRows: VotingDateRow[] = [];
  if (vd.early_voting_start || vd.early_voting_end) {
    const range =
      vd.early_voting_start && vd.early_voting_end
        ? `${formatDate(vd.early_voting_start, locale)} – ${formatDate(vd.early_voting_end, locale)}`
        : vd.early_voting_start
          ? t('votingInfo.votingDates.starting', {
              date: formatDate(vd.early_voting_start, locale),
            })
          : vd.early_voting_end
            ? t('votingInfo.votingDates.through', { date: formatDate(vd.early_voting_end, locale) })
            : '';
    earlyRows.push({
      key: 'earlyVoting',
      label: t('votingInfo.votingDates.earlyVoting'),
      value: range + (vd.early_voting_varies ? t('votingInfo.votingDates.mayVaryByCounty') : ''),
    });
  }

  const absenteeRows: VotingDateRow[] = [];
  if (vd.absentee_request_date)
    absenteeRows.push({
      key: 'requestAbsenteeBallotBy',
      label: t('votingInfo.votingDates.requestAbsenteeBallotBy'),
      value: `${deadline(vd.absentee_request_date, vd.absentee_request_time)}${qualifier(vd.absentee_request_postmarked_or_received)}`,
    });
  if (vd.all_mail_send_start)
    absenteeRows.push({
      key: 'mailBallotsSentStarting',
      label: t('votingInfo.votingDates.mailBallotsSentStarting'),
      value: formatDate(vd.all_mail_send_start, locale),
    });
  if (vd.absentee_return_mail_date)
    absenteeRows.push({
      key: 'returnBallotByMailBy',
      label: t('votingInfo.votingDates.returnBallotByMailBy'),
      value: `${deadline(vd.absentee_return_mail_date, vd.absentee_return_mail_time)}${qualifier(vd.absentee_postmarked_or_received)}`,
    });
  if (vd.absentee_received_by_date)
    absenteeRows.push({
      key: 'mailedBallotMustArriveBy',
      label: t('votingInfo.votingDates.mailedBallotMustArriveBy'),
      value: deadline(vd.absentee_received_by_date, vd.absentee_received_by_time),
    });
  if (vd.absentee_return_in_person_date)
    absenteeRows.push({
      key: 'returnBallotInPersonBy',
      label: t('votingInfo.votingDates.returnBallotInPersonBy'),
      value: deadline(vd.absentee_return_in_person_date, vd.absentee_return_in_person_time),
    });

  const pollsRows: VotingDateRow[] = [];
  if (vd.polls_close_time)
    pollsRows.push({
      key: 'pollsClose',
      label: t('votingInfo.votingDates.pollsClose'),
      // An unreadable value shows as sent rather than as a made-up time.
      value: formatTimeOfDay(vd.polls_close_time, locale) ?? vd.polls_close_time,
    });

  // The date that closes each row; rows without one are never marked passed.
  const closingDate: Partial<Record<VotingDateRowKey, string | undefined>> = {
    registrationDeadline: regOnline,
    registerOnlineBy: regOnline,
    registerByMailBy: regMail,
    registerInPersonBy: regInPerson,
    sameDayRegistration: vd.election_day,
    // A start-only window runs up to the election, so it closes with it.
    earlyVoting: vd.early_voting_end ?? vd.election_day,
    requestAbsenteeBallotBy: vd.absentee_request_date,
    returnBallotByMailBy: vd.absentee_return_mail_date,
    mailedBallotMustArriveBy: vd.absentee_received_by_date,
    returnBallotInPersonBy: vd.absentee_return_in_person_date,
    pollsClose: vd.election_day,
  };
  const allRows = [...regRows, ...earlyRows, ...absenteeRows, ...pollsRows];
  for (const row of allRows) {
    if (isDatePassed(closingDate[row.key], today)) row.passed = true;
  }

  const find = (key: VotingDateRowKey) => regRows.find((r) => r.key === key);
  const summaryRows = [
    find('registerOnlineBy') ?? find('registerByMailBy') ?? find('registrationDeadline'),
    find('sameDayRegistration') ?? find('registerInPersonBy'),
    earlyRows[0],
  ].filter((r): r is VotingDateRow => r !== undefined);

  return { allRows, summaryRows };
}
